import { toSlug } from './slug.util';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE IDENTITY OF ONE FILLABLE SLOT. [P4]
 *
 *  ⚠️ A DERIVED KEY IS BUILT FROM AN IDENTITY, NEVER FROM A POSITION.
 *
 *  CLAUDE.md §7 already states that rule and `slot_key` (`<dutyDate>#<shiftId>`) already
 *  follows it. This is the same rule one level down, and it exists because P4 attaches a
 *  named officer to a requirement line's allocation — and BOTH of the obvious handles move
 *  underneath it:
 *
 *    • A POSITIONAL key — `(lineIndex, allocationIndex, slotIndex)` — re-points every
 *      assignment the moment a column is inserted or reordered. A constable named for the
 *      night post silently becomes a constable named for the morning one, and nothing says
 *      so. That is EXIT CRITERION 15.
 *
 *    • `allocation._id` LOOKS stable — `arrangement.schema.ts` even advertises it as
 *      "STABLE. P4 writes a status against this id" — and IS NOT. `applyArrangementContent`
 *      rebuilds `requirement_lines` from the input array on every PATCH, minting fresh
 *      sub-document ids, and `restampArrangement` then `$set`s that whole array onto every
 *      non-detached duty. A published arrangement's requirement lines are NOT frozen (only
 *      its schedule, places and location mode are), so this happens on a routine edit.
 *      That comment was an aspiration the write path defeats; P4 corrects it in place.
 *
 *  So the key is the SEMANTIC identity of the thing being filled:
 *
 *      slotRef = <lineKey>|<allocKey>#<ordinal>
 *
 *        lineKey  = P:<postTypeId> | V:<vehicleTypeId> | A:<slug of the one-off label>
 *        allocKey = <divisionOrgUnitId>:<unitOrgUnitId ?? '*'>
 *        ordinal  = the 0-based slot within that allocation's count
 *
 *  ⚠️ THE SEPARATORS CANNOT COLLIDE WITH THE CONTENT, AND THAT IS CHECKED RATHER THAN
 *  ASSUMED. An ObjectId is hex, a slug is `[a-z0-9_]`, and none of `| : # *` appears in
 *  either — so parsing back is unambiguous. `fillRef.util.test.ts` round-trips it.
 *
 *  ⚠️ IT NEEDS ONE INVARIANT, ENFORCED IN `arrangement.schema.ts`: two requirement lines
 *  may not share a line identity, and two allocations within one line may not share an
 *  allocation identity. Without it, two columns naming the same post type would produce one
 *  key for two columns. (That refine also fixes a bug that predates P4: `toGrid` already
 *  assumed allocation identities were unique within a line and rendered only the first,
 *  while `allocatedCount` summed both.)
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** Blank means "any unit under this division" — the fast path, and most rows use it. */
export const ANY_UNIT = '*';

/** `slot_ref`'s storage bound. The longest real key is ~97 characters; this is slack. */
export const SLOT_REF_MAX_LENGTH = 200;

export type RequirementLineIdentity = {
  postTypeId?: string | null | undefined;
  vehicleTypeId?: string | null | undefined;
  adHocLabel?: string | null | undefined;
};

export type AllocationIdentity = {
  divisionOrgUnitId: string;
  unitOrgUnitId?: string | null | undefined;
};

/**
 * The identity of one requirement column.
 *
 * ⚠️ TOTAL — IT NEVER THROWS, AND THAT IS LOAD-BEARING RATHER THAN DEFENSIVE. It is called
 * from a zod `superRefine`, and a throw inside a refine escapes `safeParse` as the raw error
 * and reaches the client as an opaque 500 (MEASURED on zod 4.4.3; see `slot.util.ts`'s
 * `lastDutyDate`, which exists for the same reason).
 *
 * A line with none of the three set returns `'?'` and a one-off label with no alphanumeric
 * content returns `'A:'`. Both are degenerate, both are refused upstream — the schema's
 * `.refine` requires exactly one of the three, and the ad-hoc label must slug to something.
 * Two degenerate lines therefore collide, which the duplicate-identity refine reports as the
 * duplicate it is. Failing toward a refusal is the safe direction; failing toward a distinct
 * key would silently split one column into two.
 */
export const lineKeyOf = (line: RequirementLineIdentity): string => {
  if (line.postTypeId) return `P:${line.postTypeId}`;
  if (line.vehicleTypeId) return `V:${line.vehicleTypeId}`;
  if (line.adHocLabel !== null && line.adHocLabel !== undefined && line.adHocLabel.trim().length > 0) {
    return `A:${toSlug(line.adHocLabel)}`;
  }
  return '?';
};

/**
 * The identity of one allocation source — the SAME `<division>:<unit>` identity
 * `web/src/lib/forceGrid.ts`'s `sourceKey` already uses for a grid row, so the screen and
 * the stored slot agree about what "a source" is without either importing the other.
 */
export const allocKeyOf = (allocation: AllocationIdentity): string =>
  `${allocation.divisionOrgUnitId}:${allocation.unitOrgUnitId ?? ANY_UNIT}`;

export const slotRefOf = (lineKey: string, allocKey: string, ordinal: number): string =>
  `${lineKey}|${allocKey}#${ordinal}`;

/** The whole key from the two records it identifies, for the common case. */
export const slotRefFor = (line: RequirementLineIdentity, allocation: AllocationIdentity, ordinal: number): string =>
  slotRefOf(lineKeyOf(line), allocKeyOf(allocation), ordinal);

export type ParsedSlotRef = {
  lineKey: string;
  allocKey: string;
  ordinal: number;
};

/**
 * The inverse. `null` for anything that is not a well-formed key.
 *
 * ⚠️ `null` RATHER THAN A THROW, for the same reason `lineKeyOf` is total: a caller reading
 * a stored key it does not recognise has to be able to skip the row, not fail the request.
 */
export const parseSlotRef = (value: string): ParsedSlotRef | null => {
  const hash = value.lastIndexOf('#');
  const pipe = value.indexOf('|');
  if (hash < 0 || pipe < 0 || pipe > hash) return null;

  const ordinal = Number(value.slice(hash + 1));
  if (!Number.isInteger(ordinal) || ordinal < 0) return null;

  const lineKey = value.slice(0, pipe);
  const allocKey = value.slice(pipe + 1, hash);
  if (lineKey.length === 0 || allocKey.length === 0) return null;

  return { lineKey, allocKey, ordinal };
};

/** The division half of an allocation key, for a filter that only needs the division. */
export const divisionOfAllocKey = (allocKey: string): string => {
  const colon = allocKey.indexOf(':');
  return colon < 0 ? allocKey : allocKey.slice(0, colon);
};

/** The unit half, or `null` when the row means "any unit under this division". */
export const unitOfAllocKey = (allocKey: string): string | null => {
  const colon = allocKey.indexOf(':');
  if (colon < 0) return null;
  const unit = allocKey.slice(colon + 1);
  return unit === ANY_UNIT ? null : unit;
};

/**
 * ⚠️ WHO IS ACTUALLY BEING ASKED — the unit when one is named, the division otherwise.
 *
 * FR-5.2.7 makes the division mandatory and the unit optional, and a named unit is not a
 * refinement of the request but a redirection of it: "4 UB Men, from PS-A" is addressed to
 * PS-A, and PS-A's nodal is who answers. This is the org node P4 hangs an allocation's
 * progress, its completion and its visibility on, so it is derived ONCE, here, rather than
 * spelled `unit ?? division` at each of the five call sites that need it.
 */
export const addresseeOfAllocKey = (allocKey: string): string =>
  unitOfAllocKey(allocKey) ?? divisionOfAllocKey(allocKey);

export const addresseeOf = (allocation: AllocationIdentity): string =>
  allocation.unitOrgUnitId ?? allocation.divisionOrgUnitId;

/**
 * Every slot an allocation asks for, in order. The list a publish materialises and the list
 * a fill screen renders are THE SAME LIST, produced here, so an empty chip on the screen and
 * an empty row in the database cannot disagree about how many there are.
 */
export const slotRefsFor = (line: RequirementLineIdentity, allocation: AllocationIdentity, count: number): string[] => {
  const lineKey = lineKeyOf(line);
  const allocKey = allocKeyOf(allocation);
  const refs: string[] = [];
  for (let ordinal = 0; ordinal < count; ordinal += 1) refs.push(slotRefOf(lineKey, allocKey, ordinal));
  return refs;
};
